import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// A half-time pre-order batch release flips N orders from 'scheduled' to
// 'pending' in the same second. Each flip fires its own realtime event, and
// each event's handler would re-query and see the same unseen ids, chiming
// N times for one release. Debounce collapses the burst into a single
// trailing re-query/chime.
const NEW_ORDER_DEBOUNCE_MS = 500;

// Statuses the kitchen is chimed for: a half-time pre-order arriving
// ('scheduled') and an order to prepare now ('pending', incl. a pre-order batch
// that has just started).
const WATCHED_STATUSES = ['scheduled', 'pending'];

/** One key per (order, status) so the same order chimes again when it moves on. */
const orderKey = (o: { id: string; status: string }) => `${o.id}:${o.status}`;

export interface OrderNotificationScope {
  /** The kiosk whose orders chime. */
  operatorId: string;
  /** Where that kiosk's queue lives, for the toast's View action and the browser notification. */
  ordersPath: string;
}

/**
 * Subscribes to new scheduled/pending orders of one kiosk and fires a sound +
 * browser notification. `null` = nothing to watch (a tenant served by another
 * organization's kiosk, or no workspace yet). The queue page itself has no
 * chime of its own, so the kitchen is alerted there too.
 */
export function useNewOrderNotifications(scope: OrderNotificationScope | null) {
  const operatorId = scope?.operatorId ?? null;
  const ordersPath = scope?.ordersPath ?? '/club/orders';
  // `${id}:${status}` keys of the scheduled/pending orders already seen.
  const knownOrderKeys = useRef<Set<string>>(new Set());
  const initialLoadDone = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The channel lives in the persistent AdminLayout shell and must survive
  // navigation, so the effect below depends on the kiosk only; the path it
  // navigates to is read from a ref at click time.
  const ordersPathRef = useRef(ordersPath);
  ordersPathRef.current = ordersPath;

  // Request browser notification permission once
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const playNotificationSound = () => {
    try {
      const ctx = audioContextRef.current || new AudioContext();
      audioContextRef.current = ctx;
      const now = ctx.currentTime;
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0.5, now);
      masterGain.connect(ctx.destination);
      const frequencies = [523.25, 659.25, 783.99];
      const durations = [0.15, 0.15, 0.3];
      let offset = 0;
      frequencies.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + offset);
        gain.gain.setValueAtTime(0, now + offset);
        gain.gain.linearRampToValueAtTime(0.6, now + offset + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.01, now + offset + durations[i]);
        osc.connect(gain);
        gain.connect(masterGain);
        osc.start(now + offset);
        osc.stop(now + offset + durations[i]);
        offset += durations[i] * 0.7;
      });
    } catch {
      // ignore
    }
  };

  const sendBrowserNotification = (seatLabel: string, total: number) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      const notification = new Notification('New Order Received!', {
        body: `Seat ${seatLabel} — €${total.toFixed(2)}`,
        icon: '/favicon.png',
        tag: `idseat-order-${Date.now()}`,
        requireInteraction: true,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
        window.location.href = ordersPathRef.current;
      };
    } catch (err) {
      console.error('Browser notification error:', err);
    }
  };

  useEffect(() => {
    if (!operatorId) return;
    // A kiosk switch must not carry the previous kiosk's seen set across.
    knownOrderKeys.current = new Set();
    initialLoadDone.current = false;

    // orders.operator_id is newer than the generated types (regenerated in the
    // clean-up phase), hence the untyped client on both reads.
    type WatchedOrder = { id: string; status: string; seat_id: string | null; tag_id?: string | null; total_amount: number };

    // Seed known orders so we don't notify about existing ones
    (async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data } = (await (supabase as any)
        .from('orders')
        .select('id, status')
        .eq('operator_id', operatorId)
        .in('status', WATCHED_STATUSES)) as { data: Pick<WatchedOrder, 'id' | 'status'>[] | null };
      if (data) {
        knownOrderKeys.current = new Set(data.map(orderKey));
      }
      initialLoadDone.current = true;
    })();

    const checkForNewPendingOrders = async () => {
      if (!initialLoadDone.current) return;
      // Re-check the database to detect new scheduled/pending orders
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data } = (await (supabase as any)
        .from('orders')
        .select('id, status, seat_id, tag_id, total_amount')
        .eq('operator_id', operatorId)
        .in('status', WATCHED_STATUSES)) as { data: WatchedOrder[] | null };
      if (!data) return;
      const newOrders = data.filter(o => !knownOrderKeys.current.has(orderKey(o)));
      if (newOrders.length > 0) {
        const newest = newOrders[0];
        const allScheduled = newOrders.every(o => o.status === 'scheduled');
        const batchStarted = newOrders.some(
          o => o.status === 'pending' && knownOrderKeys.current.has(`${o.id}:scheduled`),
        );
        const message = allScheduled
          ? 'New pre-order received!'
          : batchStarted
            ? 'Pre-orders started — prepare now'
            : 'New order received!';
        playNotificationSound();
        toast.info(message, {
          icon: '🔔',
          duration: 8000,
          action: { label: 'View', onClick: () => { window.location.href = ordersPathRef.current; } },
        });
        // Resolve the place label: the seat, or the screen tag's label for a
        // seatless (generic QR) pick-up order.
        if (newest.seat_id) {
          const { data: seat } = await supabase
            .from('seats')
            .select('display_label')
            .eq('id', newest.seat_id)
            .single();
          sendBrowserNotification(
            seat?.display_label || 'New seat',
            Number(newest.total_amount) || 0
          );
        } else if (newest.tag_id) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: tag } = await (supabase as any)
            .from('tags')
            .select('label')
            .eq('id', newest.tag_id)
            .maybeSingle();
          sendBrowserNotification(
            (tag as { label?: string | null } | null)?.label || 'Screen order',
            Number(newest.total_amount) || 0
          );
        }
      }
      // Always refresh the known set so we never double-notify about an
      // order the user has already seen.
      knownOrderKeys.current = new Set(data.map(orderKey));
    };

    const channel = supabase
      .channel(`global-orders-${operatorId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders', filter: `operator_id=eq.${operatorId}` },
        () => {
          if (!initialLoadDone.current) return;
          // Batch releases fire this handler N times within the same second —
          // debounce so the burst produces one re-query and one chime.
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            debounceRef.current = null;
            void checkForNewPendingOrders();
          }, NEW_ORDER_DEBOUNCE_MS);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [operatorId]);
}
